import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  type AppUiLocale,
  type AppUiNamespace,
  persistAppUiLocale,
  readStoredAppUiLocale,
} from './appUiLocale'
import {
  ensureAppUiLocaleLoaded,
  isAppUiLocaleLoaded,
  translateAppUi,
  type TranslateParams,
} from './appUiMessages'

type AppUiI18nContextValue = {
  locale: AppUiLocale
  setLocale: (next: AppUiLocale) => void
  t: (key: string, params?: TranslateParams, namespace?: AppUiNamespace) => string
}

const AppUiI18nContext = createContext<AppUiI18nContextValue | null>(null)

const HTML_LANG: Record<AppUiLocale, string> = {
  en: 'en',
  'zh-Hant': 'zh-Hant',
  'zh-CN': 'zh-CN',
}

export function AppUiI18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppUiLocale>(() =>
    typeof window !== 'undefined' ? readStoredAppUiLocale() : 'en',
  )
  /** Bumps when a lazy locale finishes loading so translators re-resolve. */
  const [localeReadyVersion, setLocaleReadyVersion] = useState(0)

  const setLocale = useCallback((next: AppUiLocale) => {
    setLocaleState(next)
    persistAppUiLocale(next)
  }, [])

  useEffect(() => {
    let cancelled = false
    if (isAppUiLocaleLoaded(locale)) return
    ensureAppUiLocaleLoaded(locale)
      .then(() => {
        if (!cancelled) setLocaleReadyVersion((v) => v + 1)
      })
      .catch((err) => {
        console.error('Failed to load UI locale', locale, err)
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  const t = useCallback(
    (key: string, params?: TranslateParams, namespace: AppUiNamespace = 'common') =>
      translateAppUi(locale, namespace, key, params),
    // localeReadyVersion forces refresh after async locale load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, localeReadyVersion],
  )

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[locale] ?? 'en'
  }, [locale])

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])

  return <AppUiI18nContext.Provider value={value}>{children}</AppUiI18nContext.Provider>
}

export function useAppUiI18n(): AppUiI18nContextValue {
  const ctx = useContext(AppUiI18nContext)
  if (!ctx) {
    throw new Error('useAppUiI18n must be used within AppUiI18nProvider')
  }
  return ctx
}

/** Namespace-scoped translator for dashboard or client portal screens. */
export function useAppTranslation(namespace: AppUiNamespace) {
  const { locale, setLocale, t: rootT } = useAppUiI18n()
  const t = useCallback(
    (key: string, params?: TranslateParams) => rootT(key, params, namespace),
    [rootT, namespace],
  )
  return { locale, setLocale, t }
}
